var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { nanoid } from "nanoid";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import { getObjectFromEntries } from "./general";
import { readContentDataFromStorage } from ".";
import { logUiLogs } from "../service";
const UTM_PARAMS = [
    "utm_campaign",
    "utm_content",
    "utm_medium",
    "utm_source",
    "utm_term"
];
const UTM_PARAMS_UC = [
    "utm_campaign_uc",
    "utm_content_uc",
    "utm_devicepartner_uc",
    "utm_medium_uc",
    "utm_program_uc",
    "utm_source_uc",
    "utm_term_uc"
];
const HOST_NAMES_WITH_CONTENT_PARTNER = [
    "deals.today.com",
    "marketplace.today.com"
];
const MP_EXT_ID = "mp_ext_id";
const DURATION_MP_EXT_ID_DAYS = 30;
const SHOPPABLE_MOMENT_ID = "shoppableMomentId";
var OptimizelyEvents;
(function (OptimizelyEvents) {
    OptimizelyEvents["REVENUE"] = "trackRevenue";
    OptimizelyEvents["ORDERS"] = "orders";
    OptimizelyEvents["ITEMS"] = "items";
    OptimizelyEvents["TRACK_PURCHASE_SKUS"] = "trackPurchaseSkus";
})(OptimizelyEvents || (OptimizelyEvents = {}));
const logLevelMapping = {
    error: 0,
    warn: 1,
    info: 2,
    debug: 3
};
function getUtmParams(isUC) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const parameters = isUC ? UTM_PARAMS_UC : [...UTM_PARAMS, ...UTM_PARAMS_UC];
    return parameters.reduce((accumulated, param) => (Object.assign(Object.assign({}, accumulated), (urlSearchParams.has(param) && {
        [param]: urlSearchParams.get(param)
    }))), {});
}
function getShoppableMomentId() {
    var _a;
    const url = new URL(window.location.href);
    const shoppableMomentId = decodeURIComponent((_a = url.searchParams.get(SHOPPABLE_MOMENT_ID)) !== null && _a !== void 0 ? _a : "");
    if (shoppableMomentId) {
        return { shoppableMomentId };
    }
    return {};
}
function getPageSlug(str) {
    var _a;
    const arr = str.split("/").filter(e => e);
    return (_a = arr[arr.length - 1]) !== null && _a !== void 0 ? _a : "";
}
function getCustomAttributes(analyticsData) {
    return Object.entries(analyticsData)
        .map(([key, value]) => `${key}:${value || ""}`)
        .join(";");
}
function parseCustomAttributes(attributes) {
    return getObjectFromEntries(attributes.split(";").map(pair => pair.split(":")));
}
function getProductInfoAttributes(product) {
    return {
        productId: `${product === null || product === void 0 ? void 0 : product.channelKey}:${product.productKey}`,
        productName: (product === null || product === void 0 ? void 0 : product.name) || null,
        sellerId: (product === null || product === void 0 ? void 0 : product.channelKey) || null,
        sellerName: (product === null || product === void 0 ? void 0 : product.seller) || null,
        sponsorName: (product === null || product === void 0 ? void 0 : product.brandName) || null
    };
}
function getSponsorEventAttributes(parsedAnalyticsDataAttributes, productInfoAttributes) {
    const { contentName, contentType } = readContentDataFromStorage();
    return Object.assign(Object.assign(Object.assign(Object.assign({ contentName,
        contentType }, productInfoAttributes), parsedAnalyticsDataAttributes), getShoppableMomentId()), getUtmParams(true));
}
function addOptimizelyEvent(eventName, value) {
    window.optimizely = window.optimizely || [];
    const tags = eventName === OptimizelyEvents.REVENUE
        ? {
            revenue: value
        }
        : { value };
    window.optimizely.push({
        type: "event",
        eventName,
        tags
    });
}
function logError(error, code, logLevel = "error") {
    var _a, _b, _c, _d, _e, _f;
    return __awaiter(this, void 0, void 0, function* () {
        if (!((_c = (_b = (_a = window.uscWidget) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.uiLogs) === null || _c === void 0 ? void 0 : _c.enabled)) {
            return;
        }
        try {
            const configLogLevel = ((_f = (_e = (_d = window.uscWidget) === null || _d === void 0 ? void 0 : _d.config) === null || _e === void 0 ? void 0 : _e.uiLogs) === null || _f === void 0 ? void 0 : _f.logLevel) || "error";
            if (logLevelMapping[logLevel] <= logLevelMapping[configLogLevel]) {
                yield logUiLogs(window === null || window === void 0 ? void 0 : window.uscWidget.config.networkBrand, {
                    logMessage: error.message + error.stack,
                    code,
                    logLevel
                });
            }
        }
        catch (e) {
            console.error("USC: Logging error to logs service failed: ", e.message);
        }
    });
}
export function logEmptyContentPartnerError(contentPartner, eventName, metadata) {
    if (!contentPartner &&
        HOST_NAMES_WITH_CONTENT_PARTNER.includes(window.location.hostname)) {
        logError(new Error(`ContentPartner on domain ${window.location.hostname} (ON ${eventName}): on product with: ${JSON.stringify(metadata)} was not found.`), "EMPTY_CONTENT_PARTNER");
    }
}
/**
 * Checks if hostnames are the same for provided urls
 */
function areTheSameHostnames(strUrlA, strUrlB) {
    let urlA;
    let urlB;
    if (strUrlA === "" || strUrlB === "") {
        return undefined;
    }
    try {
        urlA = new URL(strUrlA);
        urlB = new URL(strUrlB);
    }
    catch (error) {
        const err = error;
        logError(err, "INVALID_URL");
        return undefined;
    }
    if (urlA !== undefined && urlB !== undefined) {
        return urlA.hostname === urlB.hostname;
    }
    return undefined;
}
function enabled() {
    return !!window.mParticle;
}
function setReferringPage(referringPage) {
    var _a, _b;
    const currentReferringPage = (_a = window.uscWidget.uscEventAttributes.referringPage) === null || _a === void 0 ? void 0 : _a.getValue();
    if (isNil(currentReferringPage)) {
        const currentUrl = (_b = window === null || window === void 0 ? void 0 : window.location.href) !== null && _b !== void 0 ? _b : "";
        const areSame = areTheSameHostnames(currentUrl, referringPage);
        // If hosts are the same or an error occured checking hosts, we save empty string
        if (isUndefined(areSame) || areSame) {
            window.uscWidget.uscEventAttributes.referringPage.getValue = () => "";
        }
        else {
            window.uscWidget.uscEventAttributes.referringPage.getValue = () => referringPage;
        }
    }
}
function getMiraklProductCategoriesAttributes(miraklCategories) {
    var _a;
    const categoriesKeys = ((_a = miraklCategories === null || miraklCategories === void 0 ? void 0 : miraklCategories.find(category => category.miraklCategoriesKeys)) === null || _a === void 0 ? void 0 : _a.miraklCategoriesKeys) || [];
    return categoriesKeys.reduce((prevCategoryKey, categoryKey, index) => (Object.assign(Object.assign({}, prevCategoryKey), { [`L${index + 1}_category`]: categoryKey })), {});
}
function saveMpExtIdToCookie(mpExtId) {
    const date = new Date();
    date.setDate(date.getDate() + DURATION_MP_EXT_ID_DAYS);
    document.cookie = `${MP_EXT_ID}=${mpExtId}; expires=${date.toString()}`;
}
function getMpExtIdFromCookie() {
    const mpExtIdCookie = document.cookie
        .split(";")
        .find(cookie => cookie.includes(MP_EXT_ID));
    return mpExtIdCookie && mpExtIdCookie.split("=")[1];
}
function getMparticleId() {
    var _a, _b, _c, _d, _e;
    return (((_b = (_a = window.mParticle) === null || _a === void 0 ? void 0 : _a.Identity) === null || _b === void 0 ? void 0 : _b.getCurrentUser) &&
        ((_e = (_d = (_c = window.mParticle) === null || _c === void 0 ? void 0 : _c.Identity) === null || _d === void 0 ? void 0 : _d.getCurrentUser()) === null || _e === void 0 ? void 0 : _e.getMPID()));
}
function getSessionId() {
    const urlParams = new URLSearchParams(window.location.search);
    const mpExtId = urlParams.get(MP_EXT_ID);
    if (mpExtId) {
        saveMpExtIdToCookie(mpExtId);
        return mpExtId;
    }
    const mpExtIdCookie = getMpExtIdFromCookie();
    if (mpExtIdCookie) {
        return mpExtIdCookie;
    }
    return getMparticleId() || nanoid();
}
function composeAttributesForEvent(eventName) {
    const eventAttributes = {};
    if (!window.uscWidget.uscEventAttributes) {
        return eventAttributes;
    }
    for (const attribute of Object.keys(window.uscWidget.uscEventAttributes)) {
        if (window.uscWidget.uscEventAttributes[attribute].events.includes(eventName)) {
            eventAttributes[attribute] =
                window.uscWidget.uscEventAttributes[attribute].getValue();
        }
    }
    return eventAttributes;
}
export default {
    OptimizelyEvents,
    addOptimizelyEvent,
    composeAttributesForEvent,
    enabled,
    getUtmParams,
    getPageSlug,
    getMiraklProductCategoriesAttributes,
    getCustomAttributes,
    getProductInfoAttributes,
    getSponsorEventAttributes,
    getSessionId,
    getShoppableMomentId,
    logError,
    parseCustomAttributes,
    setReferringPage,
    areTheSameHostnames,
    logEmptyContentPartnerError
};
